import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { meetsRole } from '@shared/utils';
import type { UserRole } from '@shared/types';
import { NAV_ITEMS, type NavItem } from './navItems';
import { NavIcon } from './NavIcon';
import { SidebarTenant } from './SidebarTenant';
import { SidebarUser } from './SidebarUser';
import { useAuth } from '../modules/auth/hooks/useAuth';

interface SidebarProps {
  onClose?: () => void;
}

interface NavGroupProps {
  item: NavItem;
  userRole: UserRole | undefined;
  onClose?: () => void;
}

function NavGroup({ item, userRole, onClose }: NavGroupProps) {
  const location = useLocation();
  const visibleChildren = (item.children ?? []).filter(
    (child) => !child.requiredRole || meetsRole(userRole, child.requiredRole)
  );

  const isChildActive = visibleChildren.some((child) =>
    location.pathname.startsWith(child.path)
  );

  const [open, setOpen] = useState(isChildActive);

  if (visibleChildren.length === 0) return null;

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={clsx(
          'flex w-full items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
          isChildActive
            ? 'bg-blue-600 text-white'
            : 'text-gray-300 hover:bg-gray-700 hover:text-white'
        )}
      >
        <NavIcon name={item.icon} />
        <span className="flex-1 text-left">{item.label}</span>
        <svg
          className={clsx('h-4 w-4 transition-transform', open && 'rotate-180')}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="ml-4 mt-1 space-y-1 border-l border-gray-700 pl-3">
          {visibleChildren.map((child) => (
            <NavLink
              key={child.path}
              to={child.path}
              onClick={onClose}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                )
              }
            >
              <NavIcon name={child.icon} />
              {child.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export function Sidebar({ onClose }: SidebarProps) {
  const { user } = useAuth();
  const userRole = user?.role;

  return (
    <div className="flex flex-col h-full bg-gray-900 w-64">
      {/* システム名 */}
      <div className="flex items-center gap-3 px-4 py-4 border-b border-gray-700">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-600">
          <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>
        <div>
          <p className="text-white font-bold text-base leading-tight">AIRS - 販売</p>
          <p className="text-gray-400 text-xs">販売管理システム</p>
        </div>
      </div>

      {/* テナント名 */}
      <div className="pt-3">
        <SidebarTenant />
      </div>

      {/* ナビゲーション */}
      <nav className="flex-1 px-2 py-2 space-y-1 overflow-y-auto">
        {NAV_ITEMS.filter(
          (item) => !item.requiredRole || meetsRole(userRole, item.requiredRole)
        ).map((item) =>
          item.children ? (
            <NavGroup key={item.path} item={item} userRole={userRole} onClose={onClose} />
          ) : (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              onClick={onClose}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                )
              }
            >
              <NavIcon name={item.icon} />
              {item.label}
            </NavLink>
          )
        )}
      </nav>

      {/* ログインユーザー */}
      <SidebarUser />
    </div>
  );
}
